import { Injectable } from "@angular/core";
import { BigNumber } from "shared/cubiq-model-multi-asset";
import { ObjectUtils } from "@bfx/primeng/utils";
// BFX
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import {
  ClientPromiseLookupService,
  AccountPromiseLookupService,
  StaticDataPrivatizationType,
  ClientAccountVisibilityType,
} from "shared/cubiq-services";
import { BfxAutocompleteAccountModel } from "app/bfx-interface-controls/models/bfx-interface-controls.models";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

export class BfxOrderEntryAccountCacheModel {
  // must
  public clientId: string = null;
  public salesGroupId: CubiqTS.Long = null;
  public accountImpls: Array<CubiqTS.AccountImpl> = new Array<CubiqTS.AccountImpl>();
  public accounts: Array<BfxAutocompleteAccountModel> = new Array<BfxAutocompleteAccountModel>();
  public timestamp: CubiqTS.Moment = null;
}

@Injectable()
export class BfxAccountLookupMediatorService {
  /**********************************************************************/
  /* DATA  */
  private accountsByClientCache: Object = new Object(); //Object.create(null);
  private validityPeriod: number = 30; // in minutes
  private clearCacheInterval: number = 10; // in minutes

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    protected _clientLookupService: ClientPromiseLookupService,
    protected _accountLookupService: AccountPromiseLookupService,
  ) {
    this.initialize();
  }

  /**********************************************************************/
  /* Initialization  */

  private initialize(): void {
    this._accountLookupService
      .getModelUpdateSubject()
      .subscribe(this.onAccountUpdateEvent.bind(this));
    this._clientLookupService
      .getModelUpdateSubject()
      .subscribe(this.onClientUpdateEvent.bind(this));
    setInterval(() => {
      this.clearUnusedCache();
    }, this.clearCacheInterval * 1000 * 60);
  }

  private onAccountUpdateEvent(event: any): void {
    if (event != null && event.payLoad instanceof CubiqTS.AccountImpl) {
      let accountUpdate: CubiqTS.AccountImpl = event.payLoad;
      let clientId = accountUpdate.clientId;
      let salesGroupId = accountUpdate.salesGroupId;
      if (accountUpdate.ordersEnabledFg == null || accountUpdate.ordersEnabledFg) {
        // Account Active => ADD or UPDATE
        if (salesGroupId != null) {
          // break account
          for (let [key, value] of Object.entries(this.accountsByClientCache)) {
            if ((<BfxOrderEntryAccountCacheModel>value).salesGroupId.equals(salesGroupId)) {
              this.executeAccountUpdateForClient(key, accountUpdate);
            }
          }
        } else if (
          typeof accountUpdate.clientId === "string" &&
          this.accountsByClientCache.hasOwnProperty(clientId)
        ) {
          this.executeAccountUpdateForClient(clientId, accountUpdate);
        }
      } // else

      if (!accountUpdate.ordersEnabledFg) {
        // Account Inactive => DELETE
        for (let [key, value] of Object.entries(this.accountsByClientCache)) {
          if (
            (salesGroupId != null && !value.salesGroupId.equals(salesGroupId)) ||
            (clientId != null && key === clientId)
          ) {
            let index = value.accountImpls.map((item) => item.id).indexOf(accountUpdate.id);
            if (index > -1) {
              this.accountsByClientCache[clientId].accountImpls.splice(index, 1);
              //
              let indexAutocomplete = value.accounts
                .map((item) => item.value)
                .indexOf(accountUpdate.id);
              if (indexAutocomplete > -1) {
                indexAutocomplete = this.accountsByClientCache[clientId].accounts.splice(
                  indexAutocomplete,
                  1,
                );
              }
            }
          }
        }
      }
    }
  }

  private executeAccountUpdateForClient(clientId: string, accountUpdate: CubiqTS.AccountImpl) {
    if (
      clientId != null &&
      this.accountsByClientCache.hasOwnProperty(clientId) &&
      accountUpdate instanceof CubiqTS.AccountImpl
    ) {
      // update impls
      let index = this.accountsByClientCache[clientId].accountImpls
        .map((item) => item.id)
        .indexOf(accountUpdate.id);
      if (index > -1) {
        this.accountsByClientCache[clientId].accountImpls[index] = accountUpdate;
      } else {
        this.accountsByClientCache[clientId].accountImpls.push(accountUpdate);
      }
      // update autocompletes
      let indexAutocomplete = this.accountsByClientCache[clientId].accounts
        .map((item) => (<BfxAutocompleteAccountModel>item).value)
        .indexOf(accountUpdate.id);
      if (indexAutocomplete > -1) {
        this.accountsByClientCache[clientId].accounts[indexAutocomplete] =
          this.accountTransformResult(accountUpdate);
      } else {
        this.accountsByClientCache[clientId].accounts.push(
          this.accountTransformResult(accountUpdate),
        );
      }
    } else {
      logger.out.error(
        `Error: _oeMediator.executeAccountUpdateForClient(): Account update failed for clientId: ${clientId}!`,
      );
    }
  }

  private onClientUpdateEvent(event: any): void {
    if (
      event != null &&
      event.payLoad instanceof CubiqTS.ClientImpl &&
      this.accountsByClientCache.hasOwnProperty(event.payLoad.clientId)
    ) {
      let clientUpdate: CubiqTS.AccountImpl = event.payLoad;
      if (clientUpdate.ordersEnabledFg == null || clientUpdate.ordersEnabledFg) {
        // Client Active => UPDATE
        this.addCacheEntry(event.payLoad);
      } else {
        // Client Inactive => DELETE
        delete this.accountsByClientCache[clientUpdate.clientId];
      }
    }
  }
  /**********************************************************************/
  /* Public API  */
  public processClient(client: string | CubiqTS.ClientImpl): void {
    if (client != null) {
      let clientId = typeof client === "string" ? client : client.clientId;
      if (!this.accountsByClientCache.hasOwnProperty(clientId)) {
        this.addCacheEntry(client);
      }
    }
  }

  public getAccountsForClient(
    client: string | CubiqTS.ClientImpl,
    transformed: boolean = false,
  ): Promise<Array<CubiqTS.AccountImpl | BfxAutocompleteAccountModel>> {
    return new Promise<Array<CubiqTS.AccountImpl | BfxAutocompleteAccountModel>>((res, rej) => {
      if (client != null) {
        let clientId = typeof client === "string" ? client : client.clientId;
        let entry: BfxOrderEntryAccountCacheModel = this.accountsByClientCache[clientId];
        if (entry != null) {
          transformed ? res(entry.accounts) : res(entry.accountImpls);
        } else {
          this.addCacheEntryAndReturnClientId(client).then(
            (processedClientId: string) => {
              let processedEntry: BfxOrderEntryAccountCacheModel =
                this.accountsByClientCache[processedClientId];
              transformed ? res(processedEntry.accounts) : res(processedEntry.accountImpls);
            },
            (err) => res(null),
          );
        }
      } else {
        res(null);
      }
    });
  }

  // not in use
  /* 	public getAccount(accountId: string): Promise<CubiqTS.AccountImpl>{
		return new Promise<CubiqTS.AccountImpl>((res,rej)=>{
			if(accountId != null){
				for (let key of this.accountsByClientCache) {
					for(let account of this.accountsByClientCache[key].accountImpls){
						if(account.id === accountId ){
							res(account);
							return;
						}
					};
				};
				this.lookupAccountOnWorker(accountId).then(result=>res(result), err=>res(null));
			}else{
				res(null);
			}
		});
	}  */

  /* Same as getAccount but avoids searching the entire cache when client is known */
  public getAccountOfClient(
    accountId: string,
    client: string | CubiqTS.ClientImpl,
  ): Promise<CubiqTS.AccountImpl> {
    return new Promise<CubiqTS.AccountImpl>((res, rej) => {
      if (accountId != null && client != null) {
        let clientId = typeof client === "string" ? client : client.clientId;
        if (this.accountsByClientCache.hasOwnProperty(clientId)) {
          for (let account of this.accountsByClientCache[clientId].accountImpls) {
            if (account.id === accountId) {
              res(account);
              return;
            }
          }
        }
        // Fallback
        this.lookupAccountOnWorker(accountId).then(
          (result) => res(result),
          (err) => res(null),
        );
      } else {
        res(null);
      }
    });
  }

  /**********************************************************************/
  /* Cache  */

  private addCacheEntry(client: string | CubiqTS.ClientImpl, removeIfStored: boolean = true): void {
    if (client != null) {
      let clientId = typeof client === "string" ? client : client.clientId;
      this.lookupBreakAndClientAccounts(clientId).then(
        (accountImpls: Array<CubiqTS.AccountImpl>) => {
          let cacheEntry = new BfxOrderEntryAccountCacheModel();
          cacheEntry.timestamp = CubiqTS.moment();
          cacheEntry.accountImpls = accountImpls;
          cacheEntry.accounts = this.accountTransformResults(accountImpls);
          cacheEntry.clientId = clientId;

          if (removeIfStored && this.accountsByClientCache.hasOwnProperty(clientId)) {
            delete this.accountsByClientCache[clientId];
          }

          if (client instanceof CubiqTS.ClientImpl) {
            cacheEntry.salesGroupId = client.salesGroupId;
            this.accountsByClientCache = {
              ...this.accountsByClientCache,
              [clientId]: cacheEntry,
            };
          } else {
            this.lookupClientOnWorker(clientId).then((clientImpl: CubiqTS.ClientImpl) => {
              cacheEntry.salesGroupId = clientImpl.salesGroupId;
              this.accountsByClientCache = {
                ...this.accountsByClientCache,
                [clientId]: cacheEntry,
              };
            });
          }
        },
        (err) => {
          logger.out.error(
            `ERROR: _oeMediator.addCacheEntry():  Entry ERROR for client: ${clientId} | error: `,
            err,
          );
        },
      );
    } else {
      logger.out.error(`ERROR: _oeMediator.addCacheEntry(): Invalid client!`);
    }
  }

  private addCacheEntryAndReturnClientId(client: string | CubiqTS.ClientImpl): Promise<string> {
    return new Promise<string>((res, rej) => {
      if (client != null) {
        let clientId = typeof client === "string" ? client : client.clientId;
        this.lookupBreakAndClientAccounts(clientId).then(
          (accountImpls: Array<CubiqTS.AccountImpl>) => {
            let cacheEntry = new BfxOrderEntryAccountCacheModel();
            cacheEntry.timestamp = CubiqTS.moment();
            cacheEntry.accountImpls = accountImpls;
            cacheEntry.accounts = this.accountTransformResults(accountImpls);
            cacheEntry.clientId = clientId;

            if (client instanceof CubiqTS.ClientImpl) {
              cacheEntry.salesGroupId = client.salesGroupId;
              this.accountsByClientCache = {
                ...this.accountsByClientCache,
                [clientId]: cacheEntry,
              };
              res(clientId);
            } else {
              this.lookupClientOnWorker(clientId).then((clientImpl: CubiqTS.ClientImpl) => {
                cacheEntry.salesGroupId = clientImpl.salesGroupId;
                this.accountsByClientCache = {
                  ...this.accountsByClientCache,
                  [clientId]: cacheEntry,
                };
                res(clientId);
              });
            }
          },
          (err) => {
            logger.out.error(
              `ERROR: _oeMediator.addCacheEntryAndReturnClientId(): Entry ERROR for client: ${clientId} | error: `,
              err,
            );
            rej(err);
          },
        );
      } else {
        logger.out.error(`ERROR: _oeMediator.addCacheEntryAndReturnClientId(): Invalid client!`);
        rej("Invalid client");
      }
    });
  }

  /**********************************************************************/
  /* Lookups  */

  private lookupBreakAndClientAccounts(
    client: string | CubiqTS.ClientImpl,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      if (client != null && typeof client === "string" && client !== "") {
        this.lookupClientOnWorker(client).then((clientResult: CubiqTS.ClientImpl) => {
          this.executeLookupBreakAndClientAccounts(clientResult).then(
            (result) => {
              res(result);
            },
            (err) => {
              rej(err);
            },
          );
        });
      } else if (client != null && client instanceof CubiqTS.ClientImpl) {
        this.executeLookupBreakAndClientAccounts(client).then(
          (result) => {
            res(result);
          },
          (err) => {
            rej(err);
          },
        );
      } else {
        rej(`Invalid clientId: '${client}'`);
      }
    });
  }

  private executeLookupBreakAndClientAccounts(
    client: CubiqTS.ClientImpl,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      let clientId: string = client.clientId;
      let clientSalesGroup: string = client.salesGroupId.toString();
      this._accountLookupService.getActiveBreakAndClientAccounts(clientId, clientSalesGroup).then(
        (resultSet) => {
          if (resultSet != null && resultSet instanceof Array && resultSet.length > 0) {
            res(resultSet);
          } else {
            rej("No data");
          }
        },
        (err) => {
          rej(err);
        },
      );
    });
  }

  private lookupClientOnWorker(clientId: string): Promise<CubiqTS.ClientImpl> {
    return new Promise<CubiqTS.ClientImpl>((res, rej) => {
      if (clientId != null && clientId !== "") {
        // Client Account
        this._clientLookupService
          .getActiveClient(clientId)
          .then((clientData: CubiqTS.ClientImpl) => {
            if (clientData != null && clientData instanceof CubiqTS.ClientImpl) {
              res(clientData);
            } else {
              rej(`Client with clientId: '${clientId}' not found.`);
            }
          });
      } else {
        rej(`Invalid clientId: '${clientId}'`);
      }
    });
  }

  private lookupAccountOnWorker(accountId: string): Promise<CubiqTS.AccountImpl> {
    return new Promise<CubiqTS.AccountImpl>((res, rej) => {
      if (accountId != null && accountId !== "") {
        // Client Account
        this._accountLookupService
          .getActiveAccount(accountId)
          .then((accountData: CubiqTS.AccountImpl) => {
            if (accountData != null && accountData instanceof CubiqTS.AccountImpl) {
              res(accountData);
            } else {
              rej(`Account with accountId: '${accountId}' not found.`);
            }
          });
      } else {
        rej(`Invalid accountId: '${accountId}'`);
      }
    });
  }

  /**********************************************************************/
  /* Timestamp  */
  private checkTimestampValid(cacheEntry: BfxOrderEntryAccountCacheModel): boolean {
    if (cacheEntry != null && cacheEntry.timestamp instanceof CubiqTS.moment) {
      let now = CubiqTS.moment();
      let expirationInterval = CubiqTS.moment.duration(this.validityPeriod, "minutes");
      return now.isBefore(cacheEntry.timestamp.add(expirationInterval));
    }
    return false;
  }

  /**********************************************************************/
  /* Utility  */

  protected clearUnusedCache(): void {
    for (let clientId in this.accountsByClientCache) {
      if (!this.checkTimestampValid(this.accountsByClientCache[clientId])) {
        delete this.accountsByClientCache[clientId];
      }
    }
  }

  protected accountTransformResults(
    data: Array<CubiqTS.AccountImpl>,
  ): Array<BfxAutocompleteAccountModel> {
    let result = Array<BfxAutocompleteAccountModel>();
    for (let itemAccountImpl of data) {
      try {
        let item = this.accountTransformResult(itemAccountImpl);
        result.push(item);
      } catch (ex) {
        logger.out.error("EXCEPTION: _oeMediator.accountTransformResults(): ", ex);
      }
    }
    return result;
  }

  private accountTransformResult(
    itemAccountImpl: CubiqTS.AccountImpl,
  ): BfxAutocompleteAccountModel {
    if (itemAccountImpl != null && itemAccountImpl instanceof CubiqTS.AccountImpl) {
      let item = new BfxAutocompleteAccountModel(); // value => id
      item.id = itemAccountImpl.id;
      item.name = itemAccountImpl.name;
      item.booking = itemAccountImpl.bookingCode;
      item.contact = itemAccountImpl.contact;
      //
      item.value = itemAccountImpl.id;
      item.label = item.id; //(item[this.data.tabActive.tab] != null && typeof item[this.data.tabActive.tab] === 'string' && item[this.data.tabActive.tab].length > 0)? item[this.data.tabActive.tab]: 'id: ' +  item.id;
      item.hasPrivatization = StaticDataPrivatizationType.hasPrivatization(itemAccountImpl);
      item.hasFullVisibility = ClientAccountVisibilityType.isFull(itemAccountImpl);
      return item;
    } else {
      throw "Can not transform, Item is null or wrong type (AccountImpl)!";
    }
  }

  public accountUpdateLabel(
    accounts: Array<BfxAutocompleteAccountModel>,
    activeTab: string,
  ): Array<BfxAutocompleteAccountModel> {
    let result = Array<BfxAutocompleteAccountModel>();
    for (let account of accounts) {
      try {
        account.label =
          account[activeTab] != null &&
          typeof account[activeTab] === "string" &&
          account[activeTab].length > 0
            ? account[activeTab]
            : "id: " + account.id;
        result.push(account);
      } catch (ex) {
        logger.out.error("EXCEPTION: _oeMediator.accountUpdateLabel(): ", ex);
      }
    }
    return result;
  }
}
